import React, { useCallback, useMemo, useState } from "react";
import "../../css/CreateEvent.css";
import {
  CustomTicketV2,
  Event,
  NotificationType,
  ThemeType,
  TicketOptionName,
  TicketType,
} from "@markit/common.types";
import { Icon } from "@iconify/react";
import { LightTheme, useTheme } from "../../hooks/useTheme";
import { Colors } from "../../utils/colors";
import SwitchToggleItem from "../SwitchToggleItem";
import {
  getTotalTicketsDistributedInGroup,
  getTotalTicketsSoldInGroup,
} from "../../utils/eventUtils/eventUtils";
import QuantityTextField from "../QuantityTextField";
import {
  isProductSpecificType,
  makeDefaultTicket,
  MARKIT_DOCS,
  productItemLabel,
  validateCustomTickets,
} from "@markit/common.utils";
import RectangleButton from "../Buttons/RectangleButton";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import { AppState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { generate } from "shortid";
import CustomTextField from "../CustomTextField";
import PopupModalContainer from "../Containers/PopupModalContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import AlertContainer from "../Containers/AlertContainer";
import { showNotificationBanner } from "../../utils/notificationUtils";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import CustomTextFieldMultiline from "../TextFields/CustomTextFieldMultiline";
import NumberInput from "../NumberInput";
import {
  DEFAULT_ORDER_PRICE,
  DEFAULT_TICKET_PRICE,
  MAX_TICKET_PRICE,
  MAX_TICKET_QUANTITY,
  MIN_TICKET_QUANTITY,
} from "../../utils/makeEvent";
import MediaPreviewItem from "../Media/MediaPreviewItem";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import MediaUploadFile from "../Media/MediaUploadFile";

interface CreateEventCustomTicketOptionProps {
  eventSettings: Event;
  updateEventSettings: (update: Partial<Event>) => void;
  ticketOption: number;
  setIsVisible: (isVisible: boolean) => void;
  hardTheme: ThemeType;
}

const CreateEventCustomTicketOption = (
  props: CreateEventCustomTicketOptionProps
) => {
  const { currentEventFormQuestions } = useSelector(
    (state: AppState) => state.account
  );
  const {
    eventSettings,
    updateEventSettings,
    ticketOption,
    setIsVisible,
    hardTheme,
  } = props;
  const { theme } = useTheme(hardTheme);
  const dispatch = useDispatch();
  const [disableTicketGroup, setDisableTicketGroup] = useState(false);
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({ header: "", subHeader: "" });
  const [removeImageModal, setRemoveImageModal] = useState(false);

  const ticket = useMemo(
    () =>
      ticketOption !== -1
        ? eventSettings.customTickets[ticketOption]
        : undefined,
    [eventSettings.customTickets, ticketOption]
  );

  const [priceDisplayValue, setPriceDisplayValue] = useState<
    number | undefined
  >(ticket?.price ? ticket.price / 100 : undefined); // price in dollars
  const [minPriceDisplayValue, setMinPriceDisplayValue] = useState<
    number | undefined
  >(ticket?.minPrice ? ticket.minPrice / 100 : 0); // minPrice in dollars

  const defaultLabelExtension = useMemo(() => {
    const index = eventSettings.customTickets.findIndex(
      (tix) => tix.id === ticket?.id
    );
    if (index === 0) return "";
    const labelExtension =
      index > 0 ? index + 1 : eventSettings.customTickets.length + 1;
    return ` ${labelExtension}`;
  }, [eventSettings.customTickets, ticket?.id]);

  const isProduct = useMemo(
    () => isProductSpecificType(eventSettings.eventType),
    [eventSettings.eventType]
  );

  const ticketOptionName = useCallback(
    (type?: TicketType, flexiblePricing?: boolean) =>
      isProduct
        ? flexiblePricing
          ? TicketOptionName.PAY_WHAT_YOU_WANT
          : TicketOptionName.ORDER
        : type === TicketType.PAID
          ? flexiblePricing
            ? TicketOptionName.PAY_WHAT_YOU_WANT
            : TicketOptionName.PAID
          : type === TicketType.FREE
            ? TicketOptionName.FREE
            : TicketOptionName.REQUEST,
    [isProduct]
  );

  const [customTicket, setCustomTicket] = useState<CustomTicketV2>(
    ticket ??
      makeDefaultTicket(
        generate(),
        ticketOptionName(TicketType.FREE) + defaultLabelExtension,
        0,
        TicketType.FREE,
        0,
        1,
        isProduct ? 1 : 8,
        false,
        false,
        currentEventFormQuestions.map((q) => q.id), // add all current event form questions to the ticket type
        ""
      )
  );

  const styles = {
    placeholderText: { fontSize: 12, color: Colors.GRAY2 },
    inputContainer: { padding: "8px 14px", borderRadius: 12, width: 100 },
  };

  const itemLabel = useMemo(
    () => productItemLabel(eventSettings.eventType),
    [eventSettings.eventType]
  );

  const labelName = useMemo(
    () =>
      isProduct
        ? "orders"
        : customTicket.type === TicketType.PAID
          ? "purchases"
          : customTicket.type === TicketType.REQUEST
            ? "requests"
            : "registrations",
    [customTicket.type, isProduct]
  );

  const ticketTypeClassName = useMemo(
    () =>
      theme === LightTheme
        ? "TicketTypeSelectedLight"
        : disableTicketGroup
          ? "TicketTypeSelectedDarkDisabled"
          : "TicketTypeSelectedDark",
    [theme, disableTicketGroup]
  );

  const onExit = useCallback(
    (isAdding: boolean) => {
      showNotificationBanner(
        dispatch,
        `${itemLabel.singularCap} ${itemLabel.kindCap} ${
          isAdding ? "Added" : "Updated"
        }`,
        NotificationType.AFFIRMATIVE
      );
      setIsVisible(false);
    },
    [dispatch, itemLabel.singularCap, itemLabel.kindCap, setIsVisible]
  );

  const addCustomTicket = useCallback(() => {
    const existingTickets = [...eventSettings.customTickets, customTicket];
    const validate = validateCustomTickets(existingTickets);
    if (validate !== "") {
      setAlertText({ header: validate, subHeader: "" });
      return;
    }

    updateEventSettings({ customTickets: existingTickets });
    onExit(true);
  }, [eventSettings.customTickets, customTicket, updateEventSettings, onExit]);

  const updateCustomTicket = useCallback(() => {
    const updatedItems = [...eventSettings.customTickets];
    updatedItems[ticketOption] = {
      ...updatedItems[ticketOption],
      ...customTicket,
    };
    const validate = validateCustomTickets(updatedItems);
    if (validate !== "") {
      setAlertText({ header: validate, subHeader: "" });
      return;
    }

    updateEventSettings({ customTickets: updatedItems });
    onExit(false);
  }, [
    eventSettings.customTickets,
    ticketOption,
    customTicket,
    updateEventSettings,
    onExit,
  ]);

  useAsyncOnMount(async () => {
    // check if the user cannot delete the specified ticket option
    if (ticketOption !== -1) {
      const totalTicketsSoldInGroup = await getTotalTicketsSoldInGroup(
        eventSettings.id,
        eventSettings.customTickets[ticketOption].id,
        eventSettings.createdBy
      );
      const totalTicketsDistributedInGroup =
        await getTotalTicketsDistributedInGroup(
          eventSettings.id,
          eventSettings.customTickets[ticketOption].id
        );
      if (totalTicketsSoldInGroup + totalTicketsDistributedInGroup > 0) {
        setDisableTicketGroup(true);
      }
    }
  });

  const isDefaultTicketOptionName = useMemo(
    () =>
      customTicket.label === TicketOptionName.ORDER + defaultLabelExtension ||
      customTicket.label === TicketOptionName.FREE + defaultLabelExtension ||
      customTicket.label === TicketOptionName.PAID + defaultLabelExtension ||
      customTicket.label ===
        TicketOptionName.PAY_WHAT_YOU_WANT + defaultLabelExtension ||
      customTicket.label === TicketOptionName.REQUEST + defaultLabelExtension ||
      customTicket.label === "",
    [defaultLabelExtension, customTicket.label]
  );

  const removeImage = useCallback(() => {
    setCustomTicket((prev) => ({ ...prev, mediaURL: "" }));
  }, []);

  const selectTicketType = useCallback(
    (type: TicketType, price: number) => {
      setCustomTicket((prev) => ({
        ...prev,
        type: type,
        price: price,
        label: isDefaultTicketOptionName
          ? ticketOptionName(type) + defaultLabelExtension
          : customTicket.label,
        flexiblePricing: false,
        minPrice: 0,
      }));
    },
    [
      customTicket,
      defaultLabelExtension,
      isDefaultTicketOptionName,
      ticketOptionName,
    ]
  );

  const checkmarkCircle = useCallback(
    (showCheck: boolean, disabled: boolean) => (
      <div>
        <Icon
          icon={"ion:checkmark-circle"}
          height={19}
          style={{
            opacity: showCheck ? 1 : 0,
            color: disabled
              ? Colors.GRAY1
              : theme === LightTheme
                ? Colors.BLACK
                : Colors.WHITE,
            transition: "0.3s",
          }}
        />
      </div>
    ),
    [theme]
  );

  return (
    <>
      <PopupModalContainer
        theme={theme}
        headerComp={
          (ticketOption !== -1 ? "Edit " : "New ") +
          `${itemLabel.singularCap} ${itemLabel.kindCap}`
        }
        noExit={true}
        valueComp={
          <div className="ColumnNormal" style={{ gap: 14, maxHeight: "80vh" }}>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <div className="AlignedRowSpaced">
                <span className="bodyMedium" style={theme.PrimaryText}>
                  Name
                </span>
                <span className="smallBodySubtext">
                  {customTicket.label.length}/30
                </span>
              </div>
              <CustomTextField
                value={customTicket.label}
                placeholder="Customize name..."
                inputMode="text"
                borderRadius={12}
                onChange={(change: any) => {
                  setCustomTicket((prev) => ({
                    ...prev,
                    label: change.target.value,
                  }));
                }}
                altPadding={14}
                altMarginBottom={0}
                maxLength={30}
                eventTheme={hardTheme}
              />
            </div>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <div className="AlignedRowSpaced">
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <span className="bodyMedium" style={theme.PrimaryText}>
                    Description
                  </span>
                  <span className="smallBodySubtext">Optional</span>
                </div>
                <span className="smallBodySubtext">
                  {(customTicket.description ?? "").length}/300
                </span>
              </div>
              <CustomTextFieldMultiline
                value={customTicket.description}
                placeholder={"Give a short description..."}
                onChange={(e) => {
                  setCustomTicket((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }));
                }}
                maxLength={300}
                containerStyles={{
                  height: 80,
                  backgroundColor: Colors.TRANSPARENT,
                }}
                themeType={hardTheme}
              />
            </div>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <div className="AlignedRow" style={{ gap: 7 }}>
                <span className="bodyMedium" style={theme.PrimaryText}>
                  Image
                </span>
                <span className="smallBodySubtext">Optional</span>
              </div>
              {customTicket.mediaURL ? (
                <div style={{ width: 100 }}>
                  <MediaPreviewItem
                    mediaUrl={customTicket.mediaURL}
                    index={0}
                    size={100}
                    onSelectMedia={() => setRemoveImageModal(true)}
                    onRemoveMedia={() => setRemoveImageModal(true)}
                    altImage={
                      <img
                        src={customTicket.mediaURL}
                        alt="Ticket Image"
                        style={{
                          width: 100,
                          borderRadius: 8,
                          border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                        }}
                      />
                    }
                  />
                </div>
              ) : (
                <MediaUploadFile
                  supportedFileTypes={["PNG", "JPG", "JPEG"]}
                  onUpload={(blobUrl) =>
                    setCustomTicket((prev) => ({ ...prev, mediaURL: blobUrl }))
                  }
                  theme={theme}
                />
              )}
            </div>
            <StandardBorderedContainer
              containerStyles={{
                padding: 10,
                borderRadius: 14,
                border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                ...theme.NewPrimaryBG,
              }}
              className="ColumnNormal"
            >
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <div
                  className={
                    "AlignedRowSpacedSelect TicketType " +
                    (theme === LightTheme
                      ? "TicketTypeLight "
                      : "TicketTypeDark ") +
                    (customTicket.type === TicketType.FREE &&
                      ticketTypeClassName)
                  }
                  style={{
                    pointerEvents: disableTicketGroup ? "none" : "all",
                    borderWidth: disableTicketGroup ? 0 : 1.5,
                  }}
                  onClick={() => selectTicketType(TicketType.FREE, 0)}
                >
                  <span
                    className="bodyMedium"
                    style={{
                      color: disableTicketGroup
                        ? Colors.GRAY1
                        : theme.PrimaryText.color,
                    }}
                  >
                    Free
                  </span>
                  {checkmarkCircle(
                    customTicket.type === TicketType.FREE,
                    disableTicketGroup
                  )}
                </div>
                <div className="AlignedRowSpaced">
                  <div
                    className={
                      "AlignedRowSpacedSelect TicketType " +
                      (theme === LightTheme
                        ? "TicketTypeLight "
                        : "TicketTypeDark ") +
                      (customTicket.type === TicketType.PAID &&
                        ticketTypeClassName)
                    }
                    style={{
                      width: "100%",
                      pointerEvents: disableTicketGroup ? "none" : "all",
                      borderWidth: disableTicketGroup ? 0 : 1.5,
                    }}
                    onClick={() => {
                      setPriceDisplayValue(
                        isProduct
                          ? DEFAULT_ORDER_PRICE / 100
                          : DEFAULT_TICKET_PRICE / 100
                      );
                      selectTicketType(
                        TicketType.PAID,
                        isProduct ? DEFAULT_ORDER_PRICE : DEFAULT_TICKET_PRICE
                      );
                    }}
                  >
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      <span
                        className="bodyMedium"
                        style={{
                          color: disableTicketGroup
                            ? Colors.GRAY1
                            : theme.PrimaryText.color,
                        }}
                      >
                        Paid
                      </span>
                      <span style={styles.placeholderText}>
                        Markit charges a {isProduct ? "" : "ticketing fee & "}
                        standard Stripe processing fee.{" "}
                        <span
                          className="smallBodySubtext"
                          onClick={() => window.open(MARKIT_DOCS + "/fees")}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Learn more{" "}
                          <Icon
                            icon="ion:chevron-forward"
                            height={12}
                            style={{ color: Colors.GRAY1, marginBottom: -2 }}
                          />
                        </span>
                      </span>
                    </div>
                    {checkmarkCircle(
                      customTicket.type === TicketType.PAID,
                      disableTicketGroup
                    )}
                  </div>
                </div>
                <div
                  className={
                    "AlignedRowSpaced TicketType " +
                    (theme === LightTheme
                      ? "TicketTypeLight "
                      : disableTicketGroup
                        ? "TicketTypeDarkDisabled "
                        : "TicketTypeDark ") +
                    (customTicket.type === TicketType.REQUEST &&
                      ticketTypeClassName)
                  }
                  style={{
                    pointerEvents: disableTicketGroup ? "none" : "all",
                    borderWidth: disableTicketGroup ? 0 : 1.5,
                  }}
                  onClick={() => selectTicketType(TicketType.REQUEST, 0)}
                >
                  <div className="ColumnNormal" style={{ gap: 4 }}>
                    <span
                      className="bodyMedium"
                      style={{
                        color: disableTicketGroup
                          ? Colors.GRAY1
                          : theme.PrimaryText.color,
                      }}
                    >
                      Request
                    </span>
                    <span style={styles.placeholderText}>
                      {isProduct ? "Customers" : "Attendees"} must request{" "}
                      {isProduct ? "access" : "approval"}
                    </span>
                  </div>
                  {checkmarkCircle(
                    customTicket.type === TicketType.REQUEST,
                    disableTicketGroup
                  )}
                </div>
              </div>
            </StandardBorderedContainer>
            <div
              className="ColumnNormal"
              style={{ gap: 14, paddingBottom: 24 }}
            >
              {/** Paid ticket pricing section */}
              {customTicket.type === TicketType.PAID ? (
                <>
                  <SwitchToggleItem
                    title="Flexible Pricing"
                    description="Allow people to pay what they want"
                    toggleValue={customTicket.flexiblePricing}
                    disabled={disableTicketGroup}
                    theme={theme}
                    onChange={() => {
                      // Reset the min price to the default value
                      if (customTicket.flexiblePricing) {
                        setMinPriceDisplayValue(0);
                      }
                      setCustomTicket((prev) => ({
                        ...prev,
                        flexiblePricing: !prev.flexiblePricing,
                        label: isDefaultTicketOptionName
                          ? ticketOptionName(
                              TicketType.PAID,
                              !prev.flexiblePricing
                            ) + defaultLabelExtension
                          : customTicket.label,
                        minPrice: 0,
                      }));
                    }}
                  />
                  <div className="AlignedRowSpaced">
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      <span
                        className="bodyMedium"
                        style={{ ...theme.PrimaryText }}
                      >
                        {customTicket.flexiblePricing
                          ? "Suggested"
                          : `${itemLabel.singularCap} Price`}
                      </span>
                      <span className="smallBodySubtext">
                        {customTicket.flexiblePricing
                          ? "Specify a recommended price to pay"
                          : "Price must be at least $1"}
                      </span>
                    </div>
                    <NumberInput
                      min={1}
                      max={MAX_TICKET_PRICE / 100}
                      prefix={"$"}
                      value={priceDisplayValue}
                      onChange={(value) => {
                        setPriceDisplayValue(value);
                        setCustomTicket((prev) => ({
                          ...prev,
                          price: Math.round((value ?? 0) * 100),
                        }));
                      }}
                      containerStyles={styles.inputContainer}
                      allowDecimal
                      suppressErrorMessage
                      hardTheme={hardTheme}
                    />
                  </div>
                  {customTicket.flexiblePricing ? (
                    <div className="AlignedRowSpaced">
                      <div className="ColumnNormal" style={{ gap: 4 }}>
                        <span
                          className="bodyMedium"
                          style={{ ...theme.PrimaryText }}
                        >
                          Minimum
                        </span>
                        <span className="smallBodySubtext">
                          Specify the minimum price to pay
                        </span>
                      </div>
                      <NumberInput
                        min={0}
                        max={customTicket.price / 100}
                        isError={
                          customTicket.minPrice !== 0 &&
                          customTicket.minPrice < 100
                        }
                        prefix={"$"}
                        value={minPriceDisplayValue}
                        onChange={(value) => {
                          setMinPriceDisplayValue(value);
                          setCustomTicket((prev) => ({
                            ...prev,
                            minPrice: Math.round((value ?? 0) * 100),
                          }));
                        }}
                        containerStyles={styles.inputContainer}
                        allowDecimal
                        suppressErrorMessage
                        hardTheme={hardTheme}
                      />
                    </div>
                  ) : null}
                  <HorizontalDivider altMargin={0} eventTheme={hardTheme} />
                </>
              ) : null}
              {/** Capacity section */}
              <SwitchToggleItem
                title={`Set ${
                  customTicket.type === TicketType.REQUEST ? "Request" : ""
                } ${isProduct ? "Total in Stock" : "Capacity"}`}
                description={`Ends ${labelName} for this ${
                  itemLabel.kind
                } when quantity runs out.${
                  customTicket.type === TicketType.REQUEST
                    ? " You control how many you accept."
                    : ""
                }`}
                toggleValue={customTicket.quantityAvailable !== 0}
                onChange={() => {
                  setCustomTicket((prev) => ({
                    ...prev,
                    quantityAvailable: prev.quantityAvailable !== 0 ? 0 : 100,
                  }));
                }}
                theme={theme}
              />
              {customTicket.quantityAvailable !== 0 ? (
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  <div className="AlignedRowSpaced">
                    <div
                      className="bodyMedium"
                      style={{ ...theme.PrimaryText }}
                    >
                      {isProduct ? "Total Quantity" : "Capacity"}
                    </div>
                    <QuantityTextField
                      quantity={customTicket.quantityAvailable}
                      placeholder="100"
                      minimum={1}
                      maximum={Number.MAX_SAFE_INTEGER}
                      onChange={(e: any) => {
                        let quantityValue = parseInt(e.target.value);
                        if (Number.isNaN(quantityValue)) {
                          setCustomTicket((prev) => ({
                            ...prev,
                            quantityAvailable: Number.NaN,
                          }));
                          return;
                        }
                        if (quantityValue <= 0) {
                          quantityValue = 1;
                        }

                        setCustomTicket((prev) => {
                          return {
                            ...prev,
                            quantityAvailable: quantityValue,
                          };
                        });
                      }}
                      theme={theme}
                    />
                  </div>
                  <SwitchToggleItem
                    title={
                      isProduct ? 'Show "left in stock"' : 'Show "spots left"'
                    }
                    description={`Show the number of ${
                      isProduct ? "orders" : "spots"
                    } left to potential purchasers (i.e. "10 ${
                      isProduct ? "left in stock" : "spots left"
                    }")`}
                    toggleValue={customTicket.showSpotsLeft}
                    onChange={() =>
                      setCustomTicket((prev) => ({
                        ...prev,
                        showSpotsLeft: !prev.showSpotsLeft,
                      }))
                    }
                    theme={theme}
                    switchColor={Colors.ORANGE1}
                  />
                </div>
              ) : null}
              <HorizontalDivider altMargin={0} eventTheme={hardTheme} />
              {/** Min/max quantity section */}
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                  Set min/max {labelName} per person
                </span>
                <span className="smallBodySubtext" style={{ width: "90%" }}>
                  Number of {labelName} of this {itemLabel.kind} each person can
                  get.
                </span>
              </div>
              <div className="AlignedRow" style={{ gap: 14 }}>
                <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                  Min
                </span>
                <QuantityTextField
                  quantity={customTicket.minQuantity}
                  placeholder="1"
                  minimum={MIN_TICKET_QUANTITY}
                  maximum={MAX_TICKET_QUANTITY}
                  onChange={(e: any) => {
                    let minValue = parseInt(e.target.value);
                    if (minValue <= 0) {
                      minValue = 1;
                    }
                    setCustomTicket((prev) => ({
                      ...prev,
                      minQuantity: minValue,
                      maxQuantity:
                        minValue > customTicket.maxQuantity
                          ? minValue
                          : customTicket.maxQuantity,
                    }));
                  }}
                  theme={theme}
                />
                <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                  Max
                </span>
                <QuantityTextField
                  quantity={customTicket.maxQuantity}
                  placeholder="8"
                  minimum={MIN_TICKET_QUANTITY}
                  maximum={MAX_TICKET_QUANTITY}
                  onChange={(e: any) => {
                    let maxValue = parseInt(e.target.value);
                    if (maxValue <= 0) {
                      maxValue = 1;
                    }
                    if (maxValue > 24) {
                      maxValue = 24;
                    }
                    setCustomTicket((prev) => ({
                      ...prev,
                      maxQuantity: maxValue,
                    }));
                  }}
                  theme={theme}
                />
              </div>
              <HorizontalDivider altMargin={0} eventTheme={hardTheme} />
              {/** Hide ticket section */}
              <SwitchToggleItem
                title={`Hide ${itemLabel.singularCap} ${itemLabel.kindCap}`}
                description={`Hide this ${itemLabel.kind} on checkout`}
                toggleValue={customTicket.hideTicket}
                onChange={() => {
                  setCustomTicket((prev) => ({
                    ...prev,
                    hideTicket: !prev.hideTicket,
                  }));
                }}
                theme={theme}
              />
            </div>
          </div>
        }
        fixedBottomContent={
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <HorizontalDivider altMargin={0} eventTheme={hardTheme} />
            <div
              className="AlignedRowSpaced"
              style={{ paddingInline: 14, gap: 10 }}
            >
              <RectangleButton
                buttonLabel={"Cancel"}
                onPress={() => setIsVisible(false)}
                altColor={Colors.WHITE1}
                altTextColor={Colors.BLACK}
                altPaddingVert={14}
                containerStyles={{
                  border: `0.5px solid ${Colors.GRAY2}`,
                }}
              />
              <RectangleButton
                buttonLabel={ticketOption !== -1 ? "Save" : "Create"}
                onPress={() => {
                  ticketOption === -1
                    ? addCustomTicket()
                    : updateCustomTicket();
                }}
                altColor={theme === LightTheme ? Colors.BLACK : Colors.WHITE}
                altTextColor={
                  theme === LightTheme ? Colors.WHITE : Colors.BLACK
                }
                altPaddingVert={14}
              />
            </div>
          </div>
        }
        closeModal={() => onExit(false)}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
      <ConfirmDeleteModal
        heading={"Are you sure you want to remove the image?"}
        deleteButtonText={"Remove"}
        hideModal={!removeImageModal}
        setIsVisible={setRemoveImageModal}
        deleteOnPress={removeImage}
        theme={theme}
      />
    </>
  );
};

export default CreateEventCustomTicketOption;
